import React from 'react';
import {
  Card,
  CardActionArea,
  Box,
  Chip,
  Typography,
  useTheme,
  useMediaQuery,
  alpha,
  Tooltip,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import {
  ChevronRight,
  FiberManualRecord,
  Warning,
  Schedule,
  Lock,
  LockOpen,
  Speed,
  Pause,
  Security,
} from '@mui/icons-material';
import { Website, WebsiteStatus } from '../types/website';

interface WebsiteCardProps {
  website: Website;
  onClick?: () => void;
}

// Get color for status
const getStatusColor = (status: WebsiteStatus, theme: Theme) => {
  switch (status) {
    case 'up':
      return theme.palette.success.main;
    case 'down':
      return theme.palette.error.main;
    case 'pending':
      return theme.palette.warning.main;
    case 'paused':
      return theme.palette.grey[500];
    default:
      return theme.palette.grey[500];
  }
};

// Get color for response time
const getResponseTimeColor = (ms: number | null, theme: Theme) => {
  if (ms === null) return theme.palette.text.secondary;
  if (ms < 500) return theme.palette.success.main;
  if (ms < 1000) return theme.palette.warning.main;
  return theme.palette.error.main;
};

// Format response time for display
const formatResponseTime = (ms: number | null): string => {
  if (ms === null) return '--';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

// Get status display text
const getStatusText = (status: WebsiteStatus): string => {
  switch (status) {
    case 'up':
      return 'Up';
    case 'down':
      return 'Down';
    case 'pending':
      return 'Pending';
    case 'paused':
      return 'Paused';
    default:
      return 'Unknown';
  }
};

export function WebsiteCard({ website, onClick }: WebsiteCardProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const statusColor = getStatusColor(website.status, theme);
  const responseTimeColor = getResponseTimeColor(website.response_time_ms, theme);

  const sslExpiringSoon = website.ssl_expiry_days !== null && website.ssl_expiry_days <= 30;
  const sslExpired = website.ssl_expiry_days !== null && website.ssl_expiry_days <= 0;
  const isSlowResponse = website.response_time_ms !== null && website.response_time_ms > 1000;
  const isSSLOnly = website.probe_type === 'ssl_cert';

  // Calculate uptime display
  const uptimePercent = website.uptime_24h ?? website.uptime_7d ?? website.uptime_30d;
  const uptimeDisplay = uptimePercent !== null ? `${uptimePercent.toFixed(1)}%` : '--';

  return (
    <Card
      sx={{
        height: '100%',
        borderLeft: `4px solid ${statusColor}`,
        bgcolor:
          website.status === 'down'
            ? alpha(theme.palette.error.main, 0.03)
            : website.status === 'paused'
            ? alpha(theme.palette.grey[500], 0.03)
            : 'background.paper',
      }}
    >
      <CardActionArea
        onClick={onClick}
        sx={{
          height: '100%',
          minHeight: isMobile ? 100 : 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
        }}
      >
        {/* Top row: Status indicator + badges */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          {/* Status indicator */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <FiberManualRecord
              sx={{
                fontSize: 12,
                color: statusColor,
                ...(website.status === 'down' && {
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.4 },
                  },
                }),
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: statusColor,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {getStatusText(website.status)}
            </Typography>

            {/* Probe type indicator for SSL-only */}
            {isSSLOnly && (
              <Tooltip title="SSL Certificate Monitoring Only">
                <Security
                  sx={{
                    fontSize: 14,
                    color: theme.palette.info.main,
                    ml: 0.5,
                  }}
                />
              </Tooltip>
            )}
          </Box>

          {/* Right side badges */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Uptime percentage */}
            {uptimePercent !== null && website.status !== 'paused' && (
              <Chip
                label={uptimeDisplay}
                size="small"
                color={uptimePercent >= 99.9 ? 'success' : uptimePercent >= 99 ? 'warning' : 'error'}
                variant="outlined"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
            {isMobile && onClick && <ChevronRight color="action" sx={{ fontSize: 20 }} />}
          </Box>
        </Box>

        {/* Name and URL */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant={isMobile ? 'body1' : 'subtitle1'}
            component="div"
            noWrap
            sx={{ fontWeight: 600, lineHeight: 1.3 }}
          >
            {website.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.8rem', mt: 0.25 }}>
            {website.url}
          </Typography>
        </Box>

        {/* Stats row: Response time + SSL status */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1.5 }}>
          {/* Response time chip (not shown for paused or SSL-only) */}
          {website.status !== 'paused' && !isSSLOnly && (
            <Tooltip title={isSlowResponse ? 'Slow response time' : 'Response time'}>
              <Chip
                size="small"
                icon={<Speed sx={{ fontSize: '14px !important' }} />}
                label={formatResponseTime(website.response_time_ms)}
                sx={{
                  fontSize: '0.7rem',
                  height: 20,
                  bgcolor: alpha(responseTimeColor, 0.1),
                  color: responseTimeColor,
                  '& .MuiChip-icon': { ml: 0.5, color: responseTimeColor },
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            </Tooltip>
          )}

          {/* SSL certificate status */}
          {website.ssl_expiry_days !== null && (
            <Tooltip
              title={
                sslExpired
                  ? 'SSL certificate has expired!'
                  : sslExpiringSoon
                  ? `SSL certificate expires in ${website.ssl_expiry_days} days`
                  : website.ssl_issuer
                  ? `SSL by ${website.ssl_issuer} - ${website.ssl_expiry_days} days remaining`
                  : `SSL certificate valid for ${website.ssl_expiry_days} days`
              }
            >
              <Chip
                size="small"
                icon={
                  sslExpired ? (
                    <Warning sx={{ fontSize: '14px !important' }} />
                  ) : sslExpiringSoon ? (
                    <Schedule sx={{ fontSize: '14px !important' }} />
                  ) : (
                    <Lock sx={{ fontSize: '14px !important' }} />
                  )
                }
                label={sslExpired ? 'SSL Expired!' : `${website.ssl_expiry_days}d`}
                color={sslExpired ? 'error' : sslExpiringSoon ? 'warning' : 'default'}
                variant={sslExpired || sslExpiringSoon ? 'filled' : 'outlined'}
                sx={{
                  fontSize: '0.7rem',
                  height: 20,
                  '& .MuiChip-icon': { ml: 0.5 },
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            </Tooltip>
          )}

          {/* No SSL indicator for HTTP sites */}
          {website.ssl_expiry_days === null && !isSSLOnly && website.url.startsWith('http://') && (
            <Tooltip title="No SSL - Site uses HTTP">
              <Chip
                size="small"
                icon={<LockOpen sx={{ fontSize: '14px !important' }} />}
                label="HTTP"
                color="warning"
                variant="outlined"
                sx={{
                  fontSize: '0.7rem',
                  height: 20,
                  '& .MuiChip-icon': { ml: 0.5 },
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            </Tooltip>
          )}

          {/* Paused indicator */}
          {website.status === 'paused' && (
            <Chip
              size="small"
              icon={<Pause sx={{ fontSize: '14px !important' }} />}
              label="Monitoring Paused"
              sx={{
                fontSize: '0.7rem',
                height: 20,
                bgcolor: alpha(theme.palette.grey[500], 0.1),
                '& .MuiChip-icon': { ml: 0.5 },
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
          )}

          {/* Customer tag */}
          {website.customer_name && (
            <Chip
              size="small"
              label={website.customer_name}
              sx={{
                fontSize: '0.7rem',
                height: 20,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}
        </Box>

        {/* Error message for down sites */}
        {website.status === 'down' && website.last_error && (
          <Typography
            variant="caption"
            sx={{
              mt: 1,
              color: theme.palette.error.main,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {website.last_error}
          </Typography>
        )}
      </CardActionArea>
    </Card>
  );
}

export default WebsiteCard;
